/**
 * Contextual help — how a page tells the Help button which article documents it.
 *
 * A page calls `usePageHelp('reservations.travel-file.services')` and the header's Help
 * button opens that article instead of the landing page. The declaration sits next to
 * the component rather than in a central route→article map, because a map drifts
 * silently the moment a route changes while a declaration next to the code does not.
 */

import {
    createContext,
    lazy,
    Suspense,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode
} from 'react';

import { useLocation } from 'react-router-dom';

import { matchByRoute } from './useContextualArticle';

const HelpOverlay = lazy(() => import('../routes/Help/HelpOverlay'));

type HelpContextValue = {
    /** Article id declared by the deepest mounted page, if any. */
    helpId: string | null;
    setHelpId: (id: string | null) => void;
    /** Whether the help overlay is showing. */
    isOpen: boolean;
    open: () => void;
    close: () => void;
};

const HelpContext = createContext<HelpContextValue>({
    helpId: null,
    setHelpId: () => undefined,
    isOpen: false,
    open: () => undefined,
    close: () => undefined
});

export const HelpProvider = ({ children }: { children: ReactNode }) => {
    const [helpId, setHelpId] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const { pathname } = useLocation();

    // A page's own declaration wins; otherwise the article whose `route` matches where we
    // are. That is what gives every documented page contextual help without each one
    // having to opt in. See `useContextualArticle.ts`.
    const contextualId = helpId ?? matchByRoute(pathname);

    const value = useMemo(
        () => ({
            helpId,
            setHelpId,
            isOpen,
            open: () => setIsOpen(true),
            close: () => setIsOpen(false)
        }),
        [helpId, isOpen]
    );

    return (
        <HelpContext.Provider value={value}>
            {children}
            {/* Mounted once, at the provider, so it overlays every page and survives
                route changes without any page having to know about it. Lazy so the
                markdown renderer is not in the initial bundle — help is opened rarely
                and should not slow down first paint. */}
            <Suspense fallback={null}>
                {isOpen && <HelpOverlay open onClose={value.close} initialArticleId={contextualId} />}
            </Suspense>
        </HelpContext.Provider>
    );
};

/**
 * Declares the article that documents the calling page. Clears itself on unmount, so
 * navigating to a page that declares nothing falls back to the Help Center landing view
 * rather than leaving the previous page's article attached.
 */
export const usePageHelp = (helpId: string) => {
    const { setHelpId } = useContext(HelpContext);
    useEffect(() => {
        setHelpId(helpId);
        return () => setHelpId(null);
    }, [helpId, setHelpId]);
};

/** Reads the active page's article id — for the header Help button. */
export const useActiveHelpId = () => useContext(HelpContext).helpId;

/** Open/close state for the help overlay. */
export const useHelpOverlay = () => {
    const { isOpen, open, close, helpId } = useContext(HelpContext);
    return { isOpen, open, close, helpId };
};
